import React, { useEffect, useContext } from "react";
import { Drawer, Box, Typography, IconButton, TextField, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";
import { useFormik } from "formik";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import { MessageContext } from "../../../context/MessageContext";
import useAxiosRequests from "../../../hooks/useAxiosRequest";
import ExternalRoutes from "../../../config/ExternalRoutes";

const UserEdit = ({ open, handleClose, userData = {}, onUpdate }) => {
  const validationSchema = Yup.object({
    prenom: Yup.string().required("Le prénom est requis"),
    nom: Yup.string().required("Le nom est requis"),
    telephone: Yup.string()
      .required("Le téléphone est requis")
      .matches(/^\+?[0-9]{10,15}$/, "Numéro de téléphone invalide"),
  });

  const { showSuccess, showError } = useContext(MessageContext);
  const { sendRequest } = useAxiosRequests();

  const formik = useFormik({
    initialValues: {
      prenom: userData?.usrFirstname || "",
      nom: userData?.usrLastname || "",
      telephone: userData?.usrTel || "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const body = new FormData();
        Object.keys(values).forEach((key) => {
          if (values[key] !== null && values[key] !== undefined) {
            body.append(key, values[key]);
          }
        });

        const response = await sendRequest({
          method: "POST",
          url: ExternalRoutes.userInformation.editBo + userData?.id,
          body: body,
        });

        if (response.status === "success" && response.message) {
          showSuccess("Informations modifiées avec succès");
          onUpdate(values);
          handleClose();
        } else if (response.status !== "success" && response.message) {
          showError(response.message);
        } else {
          showError("Une erreur inattendue est survenue.");
        }
      } catch (error) {
        console.error("Erreur lors de l'envoi du formulaire :", error.message);
        showError("Une erreur est survenue lors de la mise à jour.");
      }
    },
  });

  useEffect(() => {
    if (userData) {
      formik.setValues({
        prenom: userData?.usrFirstname || "",
        nom: userData?.usrLastname || "",
        telephone: userData?.usrTel || "",
      });
    }
  }, [userData]);

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: 600, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Modifier l'utilisateur</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          {/* Champ Prénom */}
          <TextField
            fullWidth
            margin="normal"
            label="Prénom"
            name="prenom"
            value={formik.values.prenom}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.prenom && Boolean(formik.errors.prenom)}
            helperText={formik.touched.prenom && formik.errors.prenom}
            variant="outlined"
          />

          {/* Champ Nom */}
          <TextField
            fullWidth
            margin="normal"
            label="Nom"
            name="nom"
            value={formik.values.nom}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.nom && Boolean(formik.errors.nom)}
            helperText={formik.touched.nom && formik.errors.nom}
            variant="outlined"
          />

          {/* Champ Téléphone */}
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Téléphone
            </Typography>
            <PhoneInputWithCountrySelect
              className={`custom-phone-input kl-input-form kl-input-select-phone ${formik.touched.telephone && formik.errors.telephone ? "phone-error " : ""}`}
              defaultCountry="FR"
              international
              countryCallingCodeEditable={false}
              value={formik.values.telephone}
              onChange={(value) => formik.setFieldValue("telephone", value)}
              onBlur={() => formik.setFieldTouched("telephone", true)}
            />
            {formik.touched.telephone && formik.errors.telephone && (
              <Typography color="error" variant="caption">
                {formik.errors.telephone}
              </Typography>
            )}
          </Box>

          {/* Boutons Annuler et Enregistrer */}
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={handleClose} sx={{ mr: 2 }}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={!formik.isValid || formik.isSubmitting}>
              Enregistrer
            </Button>
          </Box>
        </form>
      </Box>
    </Drawer>
  );
};

export default UserEdit;